import React, { useLayoutEffect, useRef } from 'react';
import { LazyDialog, lazyDialog } from '../enquiry/lazyDialog';

// The dialog's body (steps, copy, the Radix dialog) is its own chunk, fetched on the first
// open or when a trigger is hovered or focused (preloadDemoOfferDialog).
const demoDialog = lazyDialog(() => import(/* webpackChunkName: "demo-offer-dialog" */ './DemoOfferDialogBody'));

/** Start loading the example-offer dialog now (hover / focus of a trigger). */
export const preloadDemoOfferDialog = () => demoDialog.preload();

const LOAD_ERROR = "The example couldn't open just now. Check your connection and try again.";

/**
 * The example-offer dialog ("Try an example offer →"): what a guest sees after scanning a
 * coaster, in three steps — browse, the offer, a claim code — for an invented bike shop.
 * Nothing is loaded or sent: no API call, no claim, no personal data. The code box says
 * "SAMPLE CODE" and is labelled as an example.
 *
 * Props: `open`, `onOpenChange(open)` (controlled; it opens on the first step again
 * after it has been closed), `returnFocusRef` (optional: the element to focus on close; default: the element
 * that had focus when it opened). Needs a router above (the "Browse real local offers"
 * link); DemoOfferButton wires it to a button.
 *
 * The body loads on the first open (a small "Loading…" panel shows until it arrives,
 * unless preloadDemoOfferDialog() ran earlier); focus moves to its heading once it opens.
 */
export default function DemoOfferDialog({ open, onOpenChange, returnFocusRef }) {
  const openerRef = useRef(null);
  // The opener, for closing from the loading / load-error panel (layout effect: before the
  // dialog moves focus).
  useLayoutEffect(() => {
    if (open && typeof document !== 'undefined') openerRef.current = document.activeElement;
  }, [open]);

  const closePending = () => {
    onOpenChange(false);
    const el = (returnFocusRef && returnFocusRef.current) || openerRef.current;
    if (el && el.isConnected && typeof el.focus === 'function') el.focus();
  };

  return (
    <LazyDialog
      dialog={demoDialog}
      open={open}
      onClose={closePending}
      errorMessage={LOAD_ERROR}
      render={(Body) => <Body open={open} onOpenChange={onOpenChange} returnFocusRef={returnFocusRef} />}
    />
  );
}
